import * as React from 'react';
import {useEffect, useRef, useState} from "react";
import {withNamespaces, WithNamespaces} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {FormField, Icon, Message} from "semantic-ui-react";
import CodeEditor from "../../../../core/components/CodeEditor/CodeEditor";
import {setSideBySideCell, toggleSidebarEditor} from "../../../../Layout/actions/commands";
import {makeEditorStateByNameSelector} from "../../../../Layout/selectors/sidebar-editor";
import {useElementsTree} from "../../../hooks/useElementsTree";
import {BoardId} from "../../../model/Board";
import {ElementsTree} from "../../../model/ElementsTree";
import {NodeType} from "../../../model/Graph";
import {Schema} from "../../../service/cody-wizard/schema/schema";
import {getRefVo} from "../../../service/cody-wizard/vo/get-ref-vo";
import {getSchemaRefName} from "../../../service/cody-wizard/vo/get-schema-ref-name";
import {getVoMetadata} from "../../../service/cody-wizard/vo/get-vo-metadata";
import {parse} from "../../../service/json/json-comment-wrapper";
import EditorToolbar from "./EditorToolbar";
import {makeShorthandSchemaValidationSchema} from "./Validation/shorthand-schema";


interface OwnProps {
  boardId: BoardId;
  schema: Schema;
  onSchemaChanged: (schema: Schema | undefined) => void;
  autofocus?: boolean;
  readonly?: boolean;
  sessionId?: string;
  codySuggestSlot?: JSX.Element;
}

type SchemaEditorProps = OwnProps & WithNamespaces;

const unsavedSessions: Record<string, string> = {};
let isRefOpening = false;

const SchemaEditor = (props: SchemaEditorProps) => {
  const dispatch = useDispatch();
  const elementsTree = useElementsTree(props.boardId);
  const codeEditorRef = useRef<CodeEditor|null>(null);
  const [editorHasFocus, setEditorHasFocus] = useState(false);
  const [invalidJson, setInvalidJson] = useState(false);
  const editorState = useSelector(makeEditorStateByNameSelector('schema'));

  let value = props.schema.toString();

  useEffect(() => {
    if (!codeEditorRef.current) {
      return;
    }

    let isInvalidJson = false;

    if(props.sessionId && typeof unsavedSessions[props.sessionId] === "string") {
      value = unsavedSessions[props.sessionId];
      isInvalidJson = true;
    }

    codeEditorRef.current!.initializeModel({
      fileId: `${props.sessionId}-schema.json`,
      language: "json",
      value,
      schema: props.schema.isShorthand()? makeShorthandSchemaValidationSchema(getPossibleRefs(elementsTree)) : undefined,
    });


    setInvalidJson(isInvalidJson);
  }, [value]);

  useEffect(() => {
    // This is needed to prevent wrong schemaChanged events due to onBlur firing when ref is loaded
    if(isRefOpening) {
      window.setTimeout(() => {
        isRefOpening = false;
      }, 500);
    }
  }, [props.sessionId]);

  useEffect(() => {
    if(props.autofocus && codeEditorRef.current) {
      codeEditorRef.current.focus();
    }
  }, [props.autofocus])

  const propagateChanges = (editorStr: string, silentError?: boolean): boolean => {
    if(isRefOpening) {
      return true;
    }
    if(editorStr === '') {
      props.onSchemaChanged(undefined);

      if(props.sessionId && unsavedSessions[props.sessionId]) {
        delete unsavedSessions[props.sessionId];
      }
      return true;
    }

    try {
      const changedSchema = parse(editorStr);
      props.onSchemaChanged(new Schema(changedSchema));

      if(props.sessionId && unsavedSessions[props.sessionId]) {
        delete unsavedSessions[props.sessionId];
      }

      return true
    } catch (e) {
      console.error("[CodyWizard] Schema Editor invalid JSON: ", e);
      if(!silentError) {
        setInvalidJson(true);
      }
      return false;
    }
  }

  const handleOpenInformationReference = (ref: string) => {
    const refVo = getRefVo(Schema.fromString(ref), elementsTree);

    if(refVo) {
      isRefOpening = true;
      dispatch(setSideBySideCell(refVo.getId()));
    }
  }

  return <FormField>
    {invalidJson &&
        <Message error={true} size="small" content={props.t('insp.cody_wizard.schema_editor.invalid_json') as string}
                 icon="warning" style={{display: 'flex'}}/>}

    <EditorToolbar
      leftSideSlot={props.codySuggestSlot}
      expanded={editorState.expanded}
      double={editorState.double}
      onExpandChanged={(expand, double) => dispatch(toggleSidebarEditor('schema', expand, !!double))}/>
    <CodeEditor
      containerId={`cody-schema-editor-${props.sessionId}`}
      ref={codeEditorRef}
      onOpenInformationReference={handleOpenInformationReference}
      repairJson={true}
      options={{
        fontSize: 12,
        folding: true,
        glyphMargin: false,
        lineNumbers: true,
        lineDecorationsWidth: 3,
        minimap: {
          enabled: false
        },
        formatOnPaste: true,
        scrollBeyondLastLine: false,
        mouseWheelZoom: true,
        automaticLayout: true,
        scrollbar: {
          alwaysConsumeMouseWheel: false
        },
        readOnly: props.readonly,
        fixedOverflowWidgets: true,
      }}
      className={"code editor" + (editorHasFocus ? ' focus' : '') + (editorState.expanded ? ' expanded' : '')  + (editorState.double ? ' double' : '')}
      onBlur={() => {
        if (codeEditorRef.current) {
          const changedSchemaStr = codeEditorRef.current.retrievePayload();

          const validJson = propagateChanges(changedSchemaStr);

          setEditorHasFocus(false);

          if (!validJson && props.sessionId) {
            unsavedSessions[props.sessionId] = changedSchemaStr;
          }
        }
      }}
      onFocus={() => {
        setEditorHasFocus(true);
      }}
    />
  </FormField>
};

export default withNamespaces()(SchemaEditor);

export const getPossibleRefs = (elementsTree: ElementsTree, noListSyntax?: boolean): string[] => {
  const possibleRefs: string[] = [];

  elementsTree.getFilteredElementsByType(NodeType.document).forEach(ele => {
    const voMeta = getVoMetadata(ele);

    const possibleRef = getSchemaRefName(ele, voMeta);

    if (!possibleRefs.includes(possibleRef)) {
      possibleRefs.push(possibleRef);
    }

    if (voMeta.schema && !voMeta.schema.isList() && !noListSyntax) {
      const possibleRefAsList = possibleRef + '[]';
      if (!possibleRefs.includes(possibleRefAsList)) {
        possibleRefs.push(possibleRefAsList);
      }
    }
  });

  return possibleRefs;
}
